import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { api } from '../../lib/api'
import { scheduleOverdueNotifications } from '../../lib/overdueNotifications'
import { useOverdueAlerts } from '../../lib/useOverdueAlerts'
import { formatTenantDateTime, formatTenantTime } from '../../lib/dates'

interface RentalLine {
  id: string
  bike: { id: string; code: string; type: string; model?: string }
  tariff: { id: string; name: string; color?: string | null }
  pickupPoint: { id: string; name: string }
  returnPoint?: { id: string; name: string } | null
  basePrice: string
  crossPointFee?: string
  tariffSnapshotCrossPoint?: string
  lineTotal?: string
}

interface Rental {
  id: string
  rentalNumber?: string | null
  bike?: { id: string; code: string; type: string; model?: string } | null
  lines: RentalLine[]
  customer: { phone: string; id?: string }
  manager: { id: string; name: string }
  pickupPoint: { id: string; name: string }
  actualStartAt: string
  plannedReturnAt: string
  actualReturnAt?: string | null
  pricingMode?: string
  basePrice: string
  totalPrice?: string | null
  prepaymentAmount?: string
  payments?: Array<{ amount: string; type: string }>
  discountPercent?: number | null
  customerName?: string
  documentNumber?: string
  paymentMethod?: string
  status: string
  managerNotes?: string
  requiresAttention?: boolean
}

/** Exact pre-close settlement from POST /rentals/:id/close-preview (mirrors the close). */
interface ClosePreview {
  total: number
  crossPointFee: number
  totalPaid: number
  /** Positive ⇒ top-up due from client; negative ⇒ refund to client. */
  balance: number
  settlementType: 'topup' | 'refund' | 'none'
  defaultMethod: string | null
}

interface PaymentLine { type: string; bikeCodes: string; amount: number; paymentMethod: string | null; note?: string | null; isExtraCharge?: boolean }
interface ClosedSummary {
  bikeCodes: string
  /** Имя клиента или телефон — для заголовка «Клиент: …» */
  customerLabel: string
  startAt: string; endAt: string; durationMinutes: number
  totalPrice: string
  prepaymentAmount: string
  pickupPoint: string; returnPoint?: string
  /** Межточечный сбор — показываем как строку в составе «Итого по аренде», не как отдельный платёж. */
  crossPointFee: number
  paymentLines: PaymentLine[]
  /** Фактический итоговый платёж, вычисленный сервером при закрытии (type 'none' = ровно) */
  settlement: { amount: number; type: 'topup' | 'refund' | 'none' } | null
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)} мин`
  const h = Math.floor(minutes / 60); const m = Math.round(minutes % 60)
  return m > 0 ? `${h} ч ${m} мин` : `${h} ч`
}

function formatOverdue(minutes: number): string {
  const h = Math.floor(minutes / 60); const m = Math.round(minutes % 60)
  if (h === 0) return `${m} мин`
  return m > 0 ? `${h} ч ${m} мин` : `${h} ч`
}

const PAYMENT_LABEL: Record<string, string> = { cash: '💵 Наличные', card: '💳 Безнал', transfer: '📲 Перевод' }
type PaymentMethod = 'cash' | 'card' | 'transfer'

/** Имя из аренды, иначе телефон (поле name у Customer в БД нет — имя хранится на аренде). */
function customerLabelFromRental(r: Rental): string {
  const n = r.customerName?.trim()
  return n || r.customer.phone
}

function customerLabelFromClosedApi(r: {
  customerName?: string | null
  customer?: { phone?: string } | null
}): string {
  const n = r.customerName?.trim()
  const p = r.customer?.phone ?? ''
  return n || p || '—'
}

const SETTLEMENT_EPS = 0.01

export default function ActiveRentalsPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const [tab, setTab] = useState<'active' | 'completed'>('active')
  const [closingId, setClosingId] = useState<string | null>(null)
  const [detailRental, setDetailRental] = useState<Rental | null>(null)
  const [notes, setNotes] = useState('')
  const [attention, setAttention] = useState(false)
  const [closedSummary, setClosedSummary] = useState<ClosedSummary | null>(null)
  const [bikeSearch, setBikeSearch] = useState('')
  const [showAddPayment, setShowAddPayment] = useState(false)
  const [addPaymentAmount, setAddPaymentAmount] = useState('')
  // 'extra' = доп. сбор (поломка): UI-only type → sent as a flagged top-up.
  const [addPaymentType, setAddPaymentType] = useState<'prepayment' | 'topup' | 'refund' | 'extra'>('topup')
  const [addPaymentMethod, setAddPaymentMethod] = useState<PaymentMethod>('cash')
  const [addPaymentReason, setAddPaymentReason] = useState('')
  const [showConvert, setShowConvert] = useState(false)
  const [convertDays, setConvertDays] = useState(1)
  const [showExtend, setShowExtend] = useState(false)
  const [extendDays, setExtendDays] = useState(1)
  const [extendPayment, setExtendPayment] = useState(0)
  const [showRefund, setShowRefund] = useState(false)
  const [refundReason, setRefundReason] = useState('')
  const [refundPaymentMethod, setRefundPaymentMethod] = useState<PaymentMethod | ''>('')
  const [settlementPaymentMethod, setSettlementPaymentMethod] = useState<PaymentMethod | ''>('')
  const [showOtherPointRentals, setShowOtherPointRentals] = useState(false)
  const [showSwap, setShowSwap] = useState(false)
  const [swapLineId, setSwapLineId] = useState<string | null>(null)
  const [swapNewBikeId, setSwapNewBikeId] = useState<string | null>(null)
  const [swapBikeSearch, setSwapBikeSearch] = useState('')
  /** 'qr' если текущее закрытие инициировано QR-сканером; сохраняем в state чтобы не терять после history.replaceState */
  const [pendingCloseSource, setPendingCloseSource] = useState<'qr' | 'manual'>('manual')
  const [qrCloseNotice, setQrCloseNotice] = useState<string | null>(null)
  const handledQrOpenRef = useRef<string | null>(null)
  const closeRequestRef = useRef(false)

  // Current shift to know this manager's point
  const { data: shiftData, isLoading: shiftLoading } = useQuery({
    queryKey: ['shift', 'current'],
    queryFn: () => api.get('/shifts/current').then((r) => r.data),
  })
  const myPointId: string | undefined = shiftData?.shift?.point?.id
  const myPointName: string | undefined = shiftData?.shift?.point?.name
  const myShiftId: string | undefined = shiftData?.shift?.id

  const { data, isLoading } = useQuery({
    queryKey: ['rentals', 'active', myPointId],
    queryFn: () => api.get(`/rentals?status=active&pointId=${myPointId}&limit=200`).then((r) => r.data),
    enabled: !!myPointId,
    staleTime: 20_000,
    refetchInterval: 45_000,
  })

  // Exact settlement from the backend (same math as the real close) — used so the
  // pre-close screen never disagrees with the close result (#16 mismatch fix).
  const closePreviewReturnPointId = myPointId || detailRental?.pickupPoint.id
  const { data: closePreview } = useQuery<ClosePreview>({
    queryKey: ['close-preview', detailRental?.id, closePreviewReturnPointId],
    queryFn: () => api.post(`/rentals/${detailRental!.id}/close-preview`, {
      ...(closePreviewReturnPointId ? { returnPointId: closePreviewReturnPointId } : {}),
    }).then((r) => r.data),
    enabled: !!detailRental && closingId === detailRental.id,
    staleTime: 0,
  })

  const { data: bookingsData } = useQuery({
    queryKey: ['bookings', 'pending'],
    queryFn: () => api.get('/bookings?status=pending').then((r) => r.data),
    enabled: tab === 'active',
    staleTime: 45_000,
    refetchInterval: 60_000,
  })

  const { data: allActiveData, isLoading: otherRentalsLoading } = useQuery({
    queryKey: ['rentals', 'active', 'all'],
    queryFn: () => api.get('/rentals?status=active&limit=200').then((r) => r.data),
    enabled: !!myPointId && tab === 'active' && showOtherPointRentals,
    staleTime: 30_000,
    refetchInterval: 60_000,
  })

  const { data: completedData, isLoading: completedLoading } = useQuery({
    queryKey: ['rentals', 'completed', myShiftId],
    queryFn: () => myShiftId
      ? api.get(`/rentals?status=completed&shiftId=${myShiftId}&limit=100`).then((r) => r.data)
      : Promise.resolve({ rentals: [] }),
    enabled: tab === 'completed' && !!myShiftId,
  })

  const { data: allBikesData } = useQuery({
    queryKey: ['bikes', 'all'],
    queryFn: () => api.get('/bikes').then((r) => r.data),
    enabled: showSwap,
    staleTime: 15_000,
  })

  const closeRental = useMutation({
    mutationFn: ({ id, returnPointId, source, refundPaymentMethod: rpm, settlementPaymentMethod: spm }: {
      id: string
      returnPointId?: string
      source?: 'qr' | 'manual'
      refundPaymentMethod?: string
      settlementPaymentMethod?: string
    }) =>
      api.post(`/rentals/${id}/close`, {
        returnPointId: returnPointId || undefined,
        source: source ?? 'manual',
        ...(rpm ? { refundPaymentMethod: rpm } : {}),
        ...(spm ? { settlementPaymentMethod: spm } : {}),
      }),
    onSuccess: (res, vars) => {
      qc.invalidateQueries({ queryKey: ['rentals'] })
      qc.invalidateQueries({ queryKey: ['bikes'] })
      try {
        const r = res.data?.rental
        if (!r) return
        const closingRental = rentals.find((x) => x.id === vars.id)
        const startAt = new Date(closingRental?.actualStartAt ?? r.actualStartAt)
        const endAt = new Date(r.actualReturnAt ?? Date.now())
        const bikes = (r.lines ?? []).length > 0
          ? (r.lines as RentalLine[]).map((l: RentalLine) => `#${l.bike.code}`)
          : r.bike
            ? [`#${r.bike.code}`]
            : (closingRental?.lines ?? []).map((l: RentalLine) => `#${l.bike.code}`)
        const label = closingRental ? customerLabelFromRental(closingRental) : customerLabelFromClosedApi(r)
        const pickupName = closingRental?.pickupPoint.name ?? (r as { pickupPoint?: { name?: string } }).pickupPoint?.name ?? '—'
        setClosedSummary({
          bikeCodes: bikes.join(', '),
          customerLabel: label,
          startAt: formatTenantTime(startAt.toISOString()),
          endAt: formatTenantTime(endAt.toISOString()),
          durationMinutes: (endAt.getTime() - startAt.getTime()) / 60000,
          totalPrice: String(r.totalPrice ?? closingRental?.basePrice ?? ''),
          prepaymentAmount: String(
            (r as { prepaymentAmount?: string | number | null }).prepaymentAmount ?? closingRental?.prepaymentAmount ?? '0',
          ),
          pickupPoint: pickupName,
          returnPoint: r.returnPoint?.name,
          crossPointFee: Number((r as { crossPointFee?: string | number | null }).crossPointFee ?? 0),
          paymentLines: res.data?.paymentLines ?? [],
          settlement: res.data?.settlement ?? null,
        })
      } finally {
        setClosingId(null)
        setDetailRental(null)
        setPendingCloseSource('manual')
        setRefundPaymentMethod('')
        setSettlementPaymentMethod('')
      }
    },
  })

  const saveNotes = useMutation({
    mutationFn: ({ id, notes, requiresAttention }: { id: string; notes: string; requiresAttention: boolean }) =>
      api.patch(`/rentals/${id}`, { managerNotes: notes, requiresAttention }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rentals'] })
      setDetailRental(null)
    },
  })

  const convertMutation = useMutation({
    mutationFn: ({ id, days }: { id: string; days: number }) =>
      api.post(`/rentals/${id}/convert-to-daily`, { days }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rentals'] })
      setShowConvert(false)
      setDetailRental(null)
    },
  })

  const convertToHourlyMutation = useMutation({
    mutationFn: ({ id }: { id: string }) => api.post(`/rentals/${id}/convert-to-hourly`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rentals'] })
      setDetailRental(null)
    },
  })

  const extendMutation = useMutation({
    mutationFn: ({ id, additionalDays, additionalPrepayment }: { id: string; additionalDays: number; additionalPrepayment: number }) =>
      api.post(`/rentals/${id}/extend`, { additionalDays, additionalPrepayment }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rentals'] })
      setShowExtend(false)
      setDetailRental(null)
      setExtendPayment(0)
    },
  })

  const refundMutation = useMutation({
    mutationFn: ({ id, returnPointId, reason, rpm }: { id: string; returnPointId?: string; reason: string; rpm?: string }) =>
      api.post(`/rentals/${id}/close`, { returnPointId: returnPointId || undefined, fullRefund: true, refundReason: reason, ...(rpm ? { refundPaymentMethod: rpm } : {}) }),
    onSuccess: (res, vars) => {
      qc.invalidateQueries({ queryKey: ['rentals'] })
      qc.invalidateQueries({ queryKey: ['bikes'] })
      try {
        const r = res.data?.rental
        if (r) {
          const closingRental = rentals.find((x) => x.id === vars.id)
          const startAt = new Date(closingRental?.actualStartAt ?? r.actualStartAt)
          const endAt = new Date(r.actualReturnAt ?? Date.now())
          const bikes = (r.lines ?? []).length > 0
            ? (r.lines as RentalLine[]).map((l: RentalLine) => `#${l.bike.code}`)
            : r.bike
              ? [`#${r.bike.code}`]
              : (closingRental?.lines ?? []).map((l: RentalLine) => `#${l.bike.code}`)
          const label = closingRental ? customerLabelFromRental(closingRental) : customerLabelFromClosedApi(r)
          const pickupName = closingRental?.pickupPoint.name ?? (r as { pickupPoint?: { name?: string } }).pickupPoint?.name ?? '—'
          setClosedSummary({
            bikeCodes: bikes.join(', '),
            customerLabel: label,
            startAt: formatTenantTime(startAt.toISOString()),
            endAt: formatTenantTime(endAt.toISOString()),
            durationMinutes: (endAt.getTime() - startAt.getTime()) / 60000,
            totalPrice: '0',
            prepaymentAmount: String(
              (r as { prepaymentAmount?: string | number | null }).prepaymentAmount ?? closingRental?.prepaymentAmount ?? '0',
            ),
            pickupPoint: pickupName,
            returnPoint: r.returnPoint?.name,
            crossPointFee: Number((r as { crossPointFee?: string | number | null }).crossPointFee ?? 0),
            paymentLines: res.data?.paymentLines ?? [],
            settlement: res.data?.settlement ?? null,
          })
        }
      } finally {
        setShowRefund(false)
        setRefundReason('')
        setRefundPaymentMethod('')
        setClosingId(null)
        setDetailRental(null)
        setPendingCloseSource('manual')
      }
    },
  })

  const swapMutation = useMutation({
    mutationFn: ({ rentalId, lineId, newBikeId }: { rentalId: string; lineId: string; newBikeId: string }) =>
      api.post(`/rentals/${rentalId}/swap-bike`, { lineId, newBikeId }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['rentals'] })
      qc.invalidateQueries({ queryKey: ['bikes'] })
      setShowSwap(false)
      setSwapLineId(null)
      setSwapNewBikeId(null)
      setSwapBikeSearch('')
      const updatedRental = res.data?.rental as Rental | undefined
      if (updatedRental) setDetailRental(updatedRental)
    },
  })

  const addPaymentMutation = useMutation({
    mutationFn: ({ rentalId, type, paymentMethod, amount, reason }: { rentalId: string; type: 'prepayment' | 'topup' | 'refund' | 'extra'; paymentMethod: PaymentMethod; amount: number; reason?: string }) => {
      // Доп. сбор → flagged top-up (excluded from the rental settlement, kept as revenue).
      if (type === 'extra') {
        return api.post('/payments/rental-payment', { rentalId, type: 'topup', paymentMethod, amount, isExtraCharge: true, note: reason?.trim() ? `Доп. сбор: ${reason.trim()}` : 'Доп. сбор' })
      }
      return api.post('/payments/rental-payment', { rentalId, type, paymentMethod, amount })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rentals'] })
      setShowAddPayment(false)
      setAddPaymentAmount('')
      setAddPaymentReason('')
    },
  })

  const openDetail = (r: Rental) => {
    setDetailRental(r)
    setNotes(r.managerNotes ?? '')
    setAttention(r.requiresAttention ?? false)
    setShowAddPayment(false)
    setAddPaymentAmount('')
    setShowConvert(false)
    setShowExtend(false)
    setShowRefund(false)
    setShowSwap(false)
    setSwapLineId(null)
    setSwapNewBikeId(null)
    setSwapBikeSearch('')
    setRefundReason('')
    setConvertDays(1)
    setExtendDays(1)
    setRefundPaymentMethod('')
    setSettlementPaymentMethod('')
  }

  const allRentals: Rental[] = useMemo(() => [...(data?.rentals ?? [])].sort((a, b) => {
    const aLocal = a.pickupPoint.id === myPointId ? 0 : 1
    const bLocal = b.pickupPoint.id === myPointId ? 0 : 1
    return aLocal - bLocal
  }), [data?.rentals, myPointId])
  const otherPointRentals: Rental[] = useMemo(() => ((allActiveData?.rentals ?? []) as Rental[])
    .filter((r) => !!myPointId && r.pickupPoint.id !== myPointId)
    .sort((a, b) => +new Date(b.actualStartAt) - +new Date(a.actualStartAt)), [allActiveData?.rentals, myPointId])
  const rentals: Rental[] = useMemo(() => bikeSearch.trim()
    ? allRentals.filter((r) => {
        const q = bikeSearch.trim().toLowerCase()
        const matchesLine = r.lines.some((l) => l.bike.code.toLowerCase().includes(q))
        const matchesBike = r.bike?.code?.toLowerCase().includes(q) ?? false
        const matchesPhone = r.customer.phone.includes(q)
        const matchesName = (r.customerName?.toLowerCase().includes(q) ?? false)
        return matchesLine || matchesBike || matchesPhone || matchesName
      })
    : allRentals, [allRentals, bikeSearch])

  // When navigated back from scanner with a bike id, open that rental's detail
  useEffect(() => {
    const state = location.state as { openBikeId?: string; closeSource?: 'qr' | 'manual' } | null
    if (!state?.openBikeId) return
    const bikeId = state.openBikeId
    const src = state.closeSource ?? 'manual'
    const stateKey = `${bikeId}:${src}`
    if (handledQrOpenRef.current === stateKey) return
    handledQrOpenRef.current = stateKey
    setQrCloseNotice(null)
    const match = rentals.find((r) =>
      r.lines.some((l) => l.bike.id === bikeId) || r.bike?.id === bikeId
    )
    if (match) {
      setPendingCloseSource(src)
      openDetail(match)
      // При QR-закрытии сразу переходим к шагу подтверждения (пропускаем первый клик)
      if (src === 'qr') setClosingId(match.id)
      window.history.replaceState({}, '')
      return
    }

    let cancelled = false
    const openByBikeFallback = async () => {
      try {
        const res = await api.get<{ rental?: Rental | null }>(`/rentals/by-bike/${bikeId}`)
        const fallbackRental = res.data?.rental
        if (!cancelled && fallbackRental) {
          setPendingCloseSource(src)
          openDetail(fallbackRental)
          if (src === 'qr') setClosingId(fallbackRental.id)
        } else if (!cancelled && src === 'qr') {
          setDetailRental(null)
          setClosingId(null)
          setQrCloseNotice('По этому QR активная аренда не найдена. Возможно, её уже закрыли. Обновите список или найдите велосипед по номеру.')
        }
      } catch {
        if (!cancelled && src === 'qr') {
          setDetailRental(null)
          setClosingId(null)
          setQrCloseNotice('Не удалось открыть аренду по QR. Обновите список или найдите велосипед по номеру.')
        }
      } finally {
        if (!cancelled) window.history.replaceState({}, '')
      }
    }

    void openByBikeFallback()
    return () => {
      cancelled = true
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, rentals])
  const bookings = (bookingsData?.bookings ?? []) as Array<{
    id: string
    bikeId?: string
    bike: { id: string; code: string; type: string }
    customerPhone: string
    scheduledStartAt: string
    prepaymentAmount: string
    manager: { name: string }
  }>

  // Schedule future notifications when planned return time approaches
  useEffect(() => {
    if (rentals.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      scheduleOverdueNotifications(rentals as any).catch(() => {})
    }
  }, [rentals])

  // Fire immediate notification when polling detects a rental just became overdue
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useOverdueAlerts(allRentals as any)

  if (shiftLoading || (myPointId && isLoading)) return <div className="flex items-center justify-center h-40 text-gray-400">Загрузка...</div>

  const completedRentals: Rental[] = completedData?.rentals ?? []

  return (
    <div className="p-4 space-y-3">
      {/* Tab switcher */}
      <div className="flex gap-1 bg-gray-100 rounded-2xl p-1">
        <button
          onClick={() => setTab('active')}
          className={`flex-1 text-sm font-medium py-2 rounded-xl transition-colors ${
            tab === 'active' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Активные {data?.rentals?.length ? `(${data.rentals.length})` : ''}
        </button>
        <button
          onClick={() => setTab('completed')}
          className={`flex-1 text-sm font-medium py-2 rounded-xl transition-colors ${
            tab === 'completed' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Завершённые {tab === 'completed' && completedRentals.length ? `(${completedRentals.length})` : ''}
        </button>
      </div>

      {qrCloseNotice && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-3 text-sm text-yellow-800 flex items-start justify-between gap-3">
          <span>{qrCloseNotice}</span>
          <button type="button" onClick={() => setQrCloseNotice(null)} className="text-yellow-700 font-semibold">
            OK
          </button>
        </div>
      )}

      {tab === 'completed' && (
        <div className="space-y-2">
          {completedLoading && <div className="text-center py-8 text-gray-400 text-sm">Загрузка...</div>}
          {!completedLoading && completedRentals.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">Завершённых аренд в этой смене нет</div>
          )}
          {completedRentals.map((r) => {
            const bikeCodes = r.lines.length > 0
              ? r.lines.map((l: RentalLine) => `#${l.bike.code}`).join(', ')
              : r.bike ? `#${r.bike.code}` : '—'
            const startAt = new Date(r.actualStartAt)
            const endAt = r.actualReturnAt ? new Date(r.actualReturnAt) : null
            const durationMin = endAt ? (endAt.getTime() - startAt.getTime()) / 60000 : 0
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => openDetail(r)}
                className="w-full text-left bg-white rounded-2xl border border-gray-200 p-3 space-y-1"
              >
                <div className="flex justify-between items-start">
                  <div className="font-semibold text-sm">{bikeCodes}{r.rentalNumber && <span className="ml-1.5 text-[10px] font-mono text-gray-400">#{r.rentalNumber}</span>}</div>
                  <span className="text-xs text-gray-400">{formatDuration(durationMin)}</span>
                </div>
                <div className="text-xs text-gray-500">{r.customer.phone}{r.customerName ? ` · ${r.customerName}` : ''}</div>
                <div className="text-xs text-gray-400">
                  {formatTenantTime(r.actualStartAt)}
                  {' → '}
                  {r.actualReturnAt ? formatTenantTime(r.actualReturnAt) : '—'}
                </div>
                <div className="flex justify-between items-center pt-1">
                  <span className="text-xs text-gray-400">{r.pickupPoint.name}</span>
                  <span className="font-semibold text-sm">{Number(r.totalPrice ?? r.basePrice).toFixed(0)} ₽</span>
                </div>
              </button>
            )
          })}
        </div>
      )}

      {tab === 'active' && <>
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-lg">Активные аренды ({rentals.length})</h2>
        <button
          onClick={() => navigate('/manager/scan', { state: { closeMode: true } })}
          className="flex items-center gap-1 border border-brand-400 text-brand-600 text-sm px-3 py-1.5 rounded-xl font-medium"
        >
          📷 По QR
        </button>
      </div>

      {/* Search by bike code or phone */}
      <div className="relative">
        <input
          type="text"
          value={bikeSearch}
          onChange={(e) => setBikeSearch(e.target.value)}
          placeholder="Поиск: № велосипеда, телефон или имя"
          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white pr-8"
        />
        {bikeSearch && (
          <button onClick={() => setBikeSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg leading-none">×</button>
        )}
      </div>

      {myPointId && (
        <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-gray-800">Аренды других точек</p>
              <p className="text-xs text-gray-500">Показываем отдельно, чтобы не смешивать с вашей сменой.</p>
            </div>
            <button
              onClick={() => setShowOtherPointRentals((v) => !v)}
              className="text-xs border border-gray-300 bg-white px-3 py-1.5 rounded-lg font-medium text-gray-700"
            >
              {showOtherPointRentals ? 'Скрыть' : 'Показать'}
            </button>
          </div>

          {showOtherPointRentals && (
            <div className="mt-3 space-y-2">
              {otherRentalsLoading && (
                <div className="text-xs text-gray-400">Загрузка аренд других точек...</div>
              )}

              {!otherRentalsLoading && otherPointRentals.length === 0 && (
                <div className="text-xs text-gray-400">Сейчас нет активных аренд других точек.</div>
              )}

              {!otherRentalsLoading && otherPointRentals.length > 0 && (
                <>
                  <p className="text-xs text-gray-500">
                    {otherPointRentals.length} аренд(ы). Используйте как fallback, если QR не сработал.
                  </p>
                  {otherPointRentals.map((r) => {
                    const bikes = r.lines.length > 0
                      ? r.lines.map((l) => l.bike)
                      : r.bike ? [r.bike] : []
                    const bikeLabel = bikes.map((b) => `#${b.code}`).join(', ')
                    return (
                      <button
                        key={`other-${r.id}`}
                        type="button"
                        onClick={() => openDetail(r)}
                        className="w-full text-left rounded-lg border border-blue-200 bg-blue-50/50 px-3 py-2 hover:bg-blue-100/60 transition-colors"
                      >
                        <p className="text-sm font-semibold text-gray-900">{bikeLabel || '—'}</p>
                        <p className="text-xs text-gray-600">{customerLabelFromRental(r)}</p>
                        <p className="text-xs text-gray-500">{r.pickupPoint.name} · {r.manager.name}</p>
                      </button>
                    )
                  })}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {bookings.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">📌 Забронировано</p>
          <div className="space-y-2">
            {bookings.map((b) => (
              <div key={b.id} className="bg-yellow-50 border border-yellow-200 rounded-xl p-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-sm">#{b.bike.code} · {b.bike.type}</p>
                    <p className="text-xs text-gray-600">{b.customerPhone} · {formatTenantDateTime(b.scheduledStartAt)}</p>
                    <p className="text-xs text-yellow-700 mt-0.5">Предоплата: {b.prepaymentAmount} ₽</p>
                  </div>
                  <button
                    onClick={() => navigate('/manager/rental/new', { state: { bike: { id: b.bike.id, code: b.bike.code, type: b.bike.type, qrData: '' }, booking: b } })}
                    className="text-xs bg-yellow-600 text-white px-3 py-1.5 rounded-lg font-medium"
                  >
                    Начать аренду
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!rentals.length && (
        <div className="text-center py-12 text-gray-400">
          <div className="text-5xl mb-3">🚲</div>
          <p>Активных аренд нет</p>
        </div>
      )}

      {rentals.map((r) => {
        const overdueMins = (Date.now() - new Date(r.plannedReturnAt).getTime()) / 60000
        const isOverdue = overdueMins > 0
        const bikes = r.lines.length > 0
          ? r.lines.map((l) => l.bike)
          : r.bike ? [r.bike] : []
        const bikeLabel = bikes.map((b) => `#${b.code}`).join(', ')
        const tariffColor = r.lines.length > 0 ? r.lines[0]?.tariff?.color : null
        const first = bikes[0]
        const bikeDetail = bikes.length === 1 && first
          ? `${first.type}${first.model ? ` · ${first.model}` : ''}`
          : `${bikes.length} вел.`

        return (
          <div
            key={r.id}
            className={`bg-white border rounded-2xl p-4 cursor-pointer ${isOverdue ? 'border-red-300' : r.requiresAttention ? 'border-orange-300' : 'border-gray-200'}`}
            onClick={() => openDetail(r)}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="font-bold flex items-center gap-1.5">
                  {tariffColor && <span style={{ width: 10, height: 10, backgroundColor: tariffColor, borderRadius: '50%', display: 'inline-block', flexShrink: 0 }} />}
                  {bikeLabel} · {bikeDetail}
                </p>
                <p className="text-sm text-gray-800">{customerLabelFromRental(r)}</p>
                {r.customerName?.trim() && (
                  <p className="text-xs text-gray-500">{r.customer.phone}</p>
                )}
                <p className="text-xs text-gray-400">{r.pickupPoint.name} · {r.manager.name}</p>
                {r.requiresAttention && <p className="text-xs text-orange-600 font-medium mt-0.5">⚠️ Требует внимания</p>}
                {r.managerNotes && <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[180px]">💬 {r.managerNotes}</p>}
              </div>
              <div className="text-right">
                <p className="font-semibold text-green-600">{r.basePrice} ₽</p>
                {isOverdue ? (
                  <p className="text-xs text-red-600 font-semibold">⏰ {formatOverdue(overdueMins)}</p>
                ) : (() => {
                  const returnDate = new Date(r.plannedReturnAt)
                  const isToday = returnDate.toDateString() === new Date().toDateString()
                  const timeStr = formatTenantTime(r.plannedReturnAt)
                  const dateStr = isToday ? '' : ` ${returnDate.toLocaleDateString('ru', { day: '2-digit', month: '2-digit' })}`
                  return <p className="text-xs text-gray-400">до{dateStr} {timeStr}</p>
                })()}
              </div>
            </div>
          </div>
        )
      })}

      </>}

      {/* Rental detail bottom sheet — outside tab blocks so it works on completed tab too (#19) */}
      {detailRental && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-end" onClick={() => setDetailRental(null)}>
          <div className="bg-white w-full rounded-t-3xl p-5 space-y-4 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between">
            <div>
              {(() => {
                const bikes = detailRental.lines.length > 0
                  ? detailRental.lines.map((l) => l.bike)
                  : detailRental.bike ? [detailRental.bike] : []
                return <h3 className="font-bold text-base">{bikes.map((b) => `#${b.code}`).join(', ')}{detailRental.rentalNumber && <span className="ml-2 text-xs font-mono text-gray-400 font-normal">#{detailRental.rentalNumber}</span>}</h3>
              })()}
              <p className="text-sm text-gray-800 font-medium">{customerLabelFromRental(detailRental)}</p>
              {detailRental.customerName?.trim() && (
                <p className="text-xs text-gray-500">{detailRental.customer.phone}</p>
              )}
              {detailRental.documentNumber && <p className="text-xs text-gray-400">Документ: {detailRental.documentNumber}</p>}
              <p className="text-xs text-gray-400 mt-0.5">
                {detailRental.pickupPoint.name} · с {formatTenantTime(detailRental.actualStartAt)}
                {' · '}{(() => {
                  const r = detailRental
                  const returnDate = new Date(r.plannedReturnAt)
                  const isToday = returnDate.toDateString() === new Date().toDateString()
                  const timeStr = formatTenantTime(r.plannedReturnAt)
                  return `до${isToday ? '' : ' ' + returnDate.toLocaleDateString('ru', { day: '2-digit', month: '2-digit' })} ${timeStr}`
                })()}
              </p>
              {detailRental.paymentMethod && (
                <p className="text-xs text-brand-600 font-medium mt-0.5">{PAYMENT_LABEL[detailRental.paymentMethod] ?? detailRental.paymentMethod}</p>
              )}
            </div>
            <button onClick={() => setDetailRental(null)} className="text-gray-400 text-2xl leading-none">×</button>
          </div>

            {/* Bike lines */}
            {detailRental.lines.length > 1 && (
              <div className="space-y-1">
                <p className="text-xs font-semibold text-gray-400 uppercase">Велосипеды ({detailRental.lines.length})</p>
                {detailRental.lines.map((line) => (
                  <div key={line.id} className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2">
                    <div>
                      <p className="text-sm font-medium">#{line.bike.code} · {line.bike.type}</p>
                      <p className="text-xs text-gray-400">{line.tariff.name} · {line.basePrice} ₽</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Swap bike */}
            {detailRental.status === 'active' && (
              <div className="border border-green-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => {
                    const lines = detailRental.lines
                    const next = !showSwap
                    setShowSwap(next)
                    if (next) {
                      setSwapLineId(lines.length === 1 ? (lines[0]?.id ?? null) : null)
                      setSwapNewBikeId(null)
                      setSwapBikeSearch('')
                    }
                  }}
                  className="w-full flex justify-between items-center px-4 py-3 bg-green-50 text-green-700 text-sm font-medium"
                >
                  <span>🔄 Заменить велосипед</span>
                  <span>{showSwap ? '▲' : '▼'}</span>
                </button>

                {/* Add payment without closing rental */}
                <button
                  type="button"
                  onClick={() => setShowAddPayment((v) => !v)}
                  className="w-full flex justify-between items-center px-4 py-3 bg-blue-50 text-blue-700 text-sm font-medium"
                >
                  <span>💳 Взять доп. платёж</span>
                  <span>{showAddPayment ? '▲' : '▼'}</span>
                </button>
                {showAddPayment && (
                  <div className="px-4 py-3 bg-blue-50/60 space-y-3 border-t border-blue-100">
                    <div className="grid grid-cols-2 gap-2">
                      {(['topup', 'prepayment', 'refund', 'extra'] as const).map((t) => (
                        <button key={t} type="button" onClick={() => setAddPaymentType(t)}
                          className={`py-2 rounded-xl border text-xs font-medium ${addPaymentType === t ? (t === 'extra' ? 'bg-orange-600 text-white border-orange-600' : 'bg-brand-600 text-white border-brand-600') : 'border-gray-300 text-gray-600'}`}>
                          {t === 'topup' ? 'Оплата' : t === 'prepayment' ? 'Предоплата' : t === 'refund' ? 'Возврат' : '🔧 Доп. сбор'}
                        </button>
                      ))}
                    </div>
                    {addPaymentType === 'extra' && (
                      <p className="text-[11px] text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-2 py-1.5">
                        Доп. сбор (поломка и т.п.) — берётся сверх аренды, в возврат не попадёт.
                      </p>
                    )}
                    <div className="grid grid-cols-3 gap-2">
                      {(['cash', 'card', 'transfer'] as const).map((m) => (
                        <button key={m} type="button" onClick={() => setAddPaymentMethod(m)}
                          className={`py-2 rounded-xl border text-xs font-medium ${addPaymentMethod === m ? 'bg-brand-600 text-white border-brand-600' : 'border-gray-300 text-gray-600'}`}>
                          {m === 'cash' ? '💵 Нал' : m === 'card' ? '💳 Безнал' : '📲 Перевод'}
                        </button>
                      ))}
                    </div>
                    <input type="number" min={0.01} step="0.01" value={addPaymentAmount}
                      onChange={(e) => setAddPaymentAmount(e.target.value)}
                      placeholder="Сумма, ₽"
                      className="w-full border rounded-xl px-3 py-2.5 text-sm" />
                    {addPaymentType === 'extra' && (
                      <input type="text" value={addPaymentReason}
                        onChange={(e) => setAddPaymentReason(e.target.value)}
                        placeholder="Причина (напр. сломал звонок)"
                        className="w-full border rounded-xl px-3 py-2.5 text-sm" />
                    )}
                    <button type="button"
                      disabled={addPaymentMutation.isPending || !(parseFloat(addPaymentAmount) > 0) || (addPaymentType === 'extra' && !addPaymentReason.trim())}
                      onClick={() => {
                        const amt = parseFloat(addPaymentAmount)
                        if (!(amt > 0)) return
                        addPaymentMutation.mutate({ rentalId: detailRental.id, type: addPaymentType, paymentMethod: addPaymentMethod, amount: amt, reason: addPaymentReason })
                      }}
                      className="w-full py-2.5 rounded-xl bg-brand-600 text-white text-sm font-medium disabled:opacity-50">
                      {addPaymentMutation.isPending ? 'Записываем...' : addPaymentType === 'extra' ? 'Записать доп. сбор' : 'Записать платёж'}
                    </button>
                    {addPaymentMutation.isError && (
                      <p className="text-xs text-red-600">
                        {(addPaymentMutation.error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Ошибка'}
                      </p>
                    )}
                  </div>
                )}
                {showSwap && (() => {
                  const lines = detailRental.lines
                  const freeBikes = ((allBikesData?.bikes ?? []) as Array<{ id: string; code: string; type: string; model?: string | null; status: string; tariff?: { name: string } | null; point?: { id: string; name: string } | null }>)
                    .filter((b) => b.status === 'free' && !lines.some((l) => l.bike.id === b.id) && b.point?.id === myPointId)
                  const filteredBikes = swapBikeSearch.trim()
                    ? freeBikes.filter((b) => b.code.toLowerCase().includes(swapBikeSearch.toLowerCase()) || (b.point?.name ?? '').toLowerCase().includes(swapBikeSearch.toLowerCase()))
                    : freeBikes

                  return (
                    <div className="p-4 space-y-3 bg-white">
                      {lines.length > 1 && (
                        <div>
                          <p className="text-xs font-medium text-gray-600 mb-1.5">Какой велосипед меняем?</p>
                          <div className="space-y-1">
                            {lines.map((line) => (
                              <button
                                key={line.id}
                                type="button"
                                onClick={() => { setSwapLineId(line.id); setSwapNewBikeId(null) }}
                                className={`w-full text-left px-3 py-2 rounded-xl border text-sm transition-colors ${swapLineId === line.id ? 'border-green-500 bg-green-50 text-green-800' : 'border-gray-200 text-gray-700'}`}
                              >
                                #{line.bike.code} · {line.bike.type} · {line.tariff.name} · {line.basePrice} ₽
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {swapLineId && (
                        <>
                          <p className="text-xs font-medium text-gray-600">Выберите свободный велосипед:</p>
                          <input
                            type="text"
                            value={swapBikeSearch}
                            onChange={(e) => setSwapBikeSearch(e.target.value)}
                            placeholder="Поиск по коду или точке"
                            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm"
                          />
                          {filteredBikes.length === 0 && (
                            <p className="text-xs text-gray-400 text-center py-2">Свободных велосипедов нет</p>
                          )}
                          <div className="space-y-1 max-h-48 overflow-auto">
                            {filteredBikes.map((b) => (
                              <button
                                key={b.id}
                                type="button"
                                onClick={() => setSwapNewBikeId(b.id)}
                                className={`w-full text-left px-3 py-2 rounded-xl border text-sm transition-colors ${swapNewBikeId === b.id ? 'border-green-500 bg-green-50 text-green-800' : 'border-gray-200 text-gray-700'}`}
                              >
                                <span className="font-medium">#{b.code}</span>
                                {b.type ? ` · ${b.type}` : ''}
                                {b.model ? ` · ${b.model}` : ''}
                                {b.tariff ? ` · ${b.tariff.name}` : ''}
                                {b.point ? <span className="text-gray-400"> · {b.point.name}</span> : ''}
                              </button>
                            ))}
                          </div>
                        </>
                      )}

                      {lines.length > 1 && (
                        <p className="text-xs text-gray-400">Меняйте один велосипед за раз.</p>
                      )}

                      {swapMutation.isError && (
                        <p className="text-red-500 text-xs">
                          {(swapMutation.error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Ошибка'}
                        </p>
                      )}

                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            if (!swapLineId || !swapNewBikeId) return
                            swapMutation.mutate({ rentalId: detailRental.id, lineId: swapLineId, newBikeId: swapNewBikeId })
                          }}
                          disabled={!swapLineId || !swapNewBikeId || swapMutation.isPending}
                          className="flex-1 bg-green-600 text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50"
                        >
                          {swapMutation.isPending ? 'Замена...' : 'Заменить'}
                        </button>
                        <button
                          onClick={() => { setShowSwap(false); setSwapLineId(null); setSwapNewBikeId(null); setSwapBikeSearch('') }}
                          className="px-4 border border-gray-300 rounded-xl text-sm text-gray-500"
                        >
                          Отмена
                        </button>
                      </div>
                    </div>
                  )
                })()}
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Комментарий менеджера</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Опишите нештатную ситуацию..."
                rows={3}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
              />
            </div>

            {/* Requires attention */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={attention}
                onChange={(e) => setAttention(e.target.checked)}
                className="w-5 h-5 rounded text-orange-500"
              />
              <span className="text-sm text-orange-700 font-medium">⚠️ Требует внимания управляющего</span>
            </label>

            {/* Save notes */}
            <button
              onClick={() => saveNotes.mutate({ id: detailRental.id, notes, requiresAttention: attention })}
              disabled={saveNotes.isPending}
              className="w-full border border-brand-400 text-brand-600 py-3 rounded-xl text-sm font-semibold disabled:opacity-60"
            >
              {saveNotes.isPending ? 'Сохранение...' : 'Сохранить комментарий'}
            </button>

            {/* Convert hourly → daily */}
            {detailRental.status === 'active' && detailRental.pricingMode === 'hourly' && (
              <div className="border border-blue-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setShowConvert((v) => !v)}
                  className="w-full flex justify-between items-center px-4 py-3 bg-blue-50 text-blue-700 text-sm font-medium"
                >
                  <span>🔄 Перевести в суточную</span>
                  <span>{showConvert ? '▲' : '▼'}</span>
                </button>
                {showConvert && (
                  <div className="p-4 space-y-3 bg-white">
                    <p className="text-xs text-gray-500">Тариф пересчитается с начала аренды по суточному тарифу.</p>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Количество суток</label>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setConvertDays((d) => Math.max(1, d - 1))}
                          className="w-10 h-10 rounded-xl border border-gray-300 text-lg font-bold flex items-center justify-center"
                        >−</button>
                        <span className="text-xl font-bold w-10 text-center">{convertDays}</span>
                        <button
                          onClick={() => setConvertDays((d) => Math.min(30, d + 1))}
                          className="w-10 h-10 rounded-xl border border-gray-300 text-lg font-bold flex items-center justify-center"
                        >+</button>
                      </div>
                    </div>
                    {convertMutation.isError && (
                      <p className="text-red-500 text-xs">
                        {(convertMutation.error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Ошибка'}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={() => convertMutation.mutate({ id: detailRental.id, days: convertDays })}
                        disabled={convertMutation.isPending}
                        className="flex-1 bg-blue-600 text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-60"
                      >
                        {convertMutation.isPending ? 'Конвертация...' : `Перевести (${convertDays} сут.)`}
                      </button>
                      <button onClick={() => setShowConvert(false)} className="px-4 border border-gray-300 rounded-xl text-sm text-gray-500">
                        Отмена
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Convert daily → hourly */}
            {detailRental.status === 'active' && detailRental.pricingMode === 'daily' && (
              <div className="border border-orange-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => {
                    if (window.confirm('Перевести аренду в часовой тариф? Стоимость будет пересчитана по фактически прошедшим часам.')) {
                      convertToHourlyMutation.mutate({ id: detailRental.id })
                    }
                  }}
                  disabled={convertToHourlyMutation.isPending}
                  className="w-full flex justify-between items-center px-4 py-3 bg-orange-50 text-orange-700 text-sm font-medium disabled:opacity-60"
                >
                  <span>🔁 Перевести в часовой тариф</span>
                  {convertToHourlyMutation.isPending && <span className="text-xs">...</span>}
                </button>
                {convertToHourlyMutation.isError && (
                  <p className="text-red-500 text-xs p-2">
                    {(convertToHourlyMutation.error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Ошибка'}
                  </p>
                )}
              </div>
            )}

            {/* Extend daily rental */}
            {detailRental.status === 'active' && detailRental.pricingMode === 'daily' && (
              <div className="border border-purple-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setShowExtend((v) => !v)}
                  className="w-full flex justify-between items-center px-4 py-3 bg-purple-50 text-purple-700 text-sm font-medium"
                >
                  <span>📅 Продлить аренду</span>
                  <span>{showExtend ? '▲' : '▼'}</span>
                </button>
                {showExtend && (
                  <div className="p-4 space-y-3 bg-white">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Добавить суток</label>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setExtendDays((d) => Math.max(1, d - 1))}
                          className="w-10 h-10 rounded-xl border border-gray-300 text-lg font-bold flex items-center justify-center"
                        >−</button>
                        <span className="text-xl font-bold w-10 text-center">{extendDays}</span>
                        <button
                          onClick={() => setExtendDays((d) => Math.min(30, d + 1))}
                          className="w-10 h-10 rounded-xl border border-gray-300 text-lg font-bold flex items-center justify-center"
                        >+</button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Доп. оплата (₽)</label>
                      <input
                        type="number"
                        min={0}
                        step={50}
                        value={extendPayment}
                        onChange={(e) => setExtendPayment(Math.max(0, Number(e.target.value)))}
                        className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm text-center font-bold"
                        placeholder="0"
                      />
                    </div>
                    {extendMutation.isError && (
                      <p className="text-red-500 text-xs">
                        {(extendMutation.error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Ошибка'}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={() => extendMutation.mutate({ id: detailRental.id, additionalDays: extendDays, additionalPrepayment: extendPayment })}
                        disabled={extendMutation.isPending}
                        className="flex-1 bg-purple-600 text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-60"
                      >
                        {extendMutation.isPending ? 'Продление...' : `+${extendDays} сут.`}
                      </button>
                      <button onClick={() => setShowExtend(false)} className="px-4 border border-gray-300 rounded-xl text-sm text-gray-500">
                        Отмена
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Full refund */}
            {detailRental.status === 'active' && (
            <div className="border border-red-200 rounded-xl overflow-hidden">
              {!showRefund ? (
                <button
                  onClick={() => setShowRefund(true)}
                  className="w-full px-4 py-3 bg-red-50 text-red-700 text-sm font-medium text-left"
                >
                  🔴 Возврат (обнулить аренду)
                </button>
              ) : (
                <div className="p-4 space-y-3 bg-white">
                  <p className="text-xs text-red-600 font-medium">Стоимость аренды будет обнулена, предоплата возвращена клиенту.</p>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Причина возврата (обязательно)</label>
                    <textarea
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                      rows={2}
                      placeholder="Причина..."
                      className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-300"
                    />
                  </div>
                  {/* Способ возврата денег клиенту */}
                  <div>
                    <p className="text-xs text-gray-600 mb-1.5 font-medium">Способ возврата денег клиенту:</p>
                    <div className="flex gap-2">
                      {(['cash', 'card', 'transfer'] as const).map((m) => (
                        <button key={m} onClick={() => setRefundPaymentMethod(m)}
                          className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${refundPaymentMethod === m ? 'bg-red-600 text-white border-red-600' : 'border-gray-300 text-gray-600'}`}>
                          {m === 'cash' ? '💵 Нал' : m === 'card' ? '💳 Безнал' : '📲 Перевод'}
                        </button>
                      ))}
                    </div>
                  </div>
                  {refundMutation.isError && (
                    <p className="text-red-500 text-xs">
                      {(refundMutation.error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Ошибка'}
                    </p>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        if (refundReason.trim().length < 3) return
                        refundMutation.mutate({ id: detailRental.id, returnPointId: myPointId || detailRental.pickupPoint.id, reason: refundReason, rpm: refundPaymentMethod || undefined })
                      }}
                      disabled={refundMutation.isPending || refundReason.trim().length < 3}
                      className="flex-1 bg-red-600 text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-60"
                    >
                      {refundMutation.isPending ? 'Возврат...' : 'Подтвердить возврат'}
                    </button>
                    <button onClick={() => setShowRefund(false)} className="px-4 border border-gray-300 rounded-xl text-sm text-gray-500">
                      Отмена
                    </button>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Close rental — return point fixed to manager's shift point */}
            {detailRental.status === 'active' && (closingId === detailRental.id ? (
              (() => {
                const returnPointId = myPointId || detailRental.pickupPoint.id
                const isOtherPoint = Boolean(myPointId && myPointId !== detailRental.pickupPoint.id)
                // No amounts on pre-close (any number can mislead the manager into
                // charging only that). We only tell IF a settlement is expected and
                // collect the payment method. The exact sum appears after close.
                const previewLoading = !closePreview
                const willSettle = closePreview
                  ? (closePreview.settlementType !== 'none' || closePreview.crossPointFee > SETTLEMENT_EPS)
                  : false
                return (
                  <div className="space-y-2 border-t pt-3">
                    {isOtherPoint && (
                      <div className="bg-orange-50 border border-orange-200 rounded-xl p-3">
                        <p className="text-xs text-orange-700 font-semibold">Точка возврата: {myPointName} (другая точка)</p>
                      </div>
                    )}

                    {previewLoading ? (
                      <p className="text-xs text-gray-400 py-2 text-center">Готовим закрытие…</p>
                    ) : willSettle ? (
                      <div className="rounded-xl border border-gray-300 bg-white p-3 space-y-2">
                        <p className="text-sm font-semibold text-gray-800">Планируется доплата или возврат</p>
                        <p className="text-xs text-gray-500">Точная сумма появится после закрытия. Выберите способ платежа:</p>
                        <div className="flex gap-2">
                          {(['cash', 'card', 'transfer'] as const).map((m) => {
                            const selected = settlementPaymentMethod === m
                            return (
                              <button
                                key={m}
                                type="button"
                                onClick={() => setSettlementPaymentMethod(m)}
                                className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-colors ${
                                  selected ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-600'
                                }`}
                              >
                                {m === 'cash' ? '💵 Нал' : m === 'card' ? '💳 Безнал' : '📲 Перевод'}
                              </button>
                            )
                          })}
                        </div>
                        {!settlementPaymentMethod && (
                          <p className="text-[11px] text-red-500">Выберите способ — без этого аренда не закроется</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 py-1 text-center">Аренда оплачена ровно — доплат и возвратов нет.</p>
                    )}

                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          if (closeRequestRef.current) return
                          closeRequestRef.current = true
                          // One chosen method applies to every close payment; the backend
                          // routes it to refund or top-up by the actual computed direction.
                          const chosen = settlementPaymentMethod || undefined
                          closeRental.mutate({
                            id: detailRental.id,
                            returnPointId,
                            source: pendingCloseSource,
                            refundPaymentMethod: chosen,
                            settlementPaymentMethod: chosen,
                          }, {
                            onSettled: () => { closeRequestRef.current = false },
                          })
                        }}
                        disabled={
                          closeRental.isPending ||
                          closeRequestRef.current ||
                          previewLoading ||
                          (willSettle && !settlementPaymentMethod)
                        }
                        className="flex-1 bg-green-600 text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-60"
                      >
                        {closeRental.isPending ? 'Закрытие...' : '✅ Подтвердить закрытие'}
                      </button>
                      <button onClick={() => setClosingId(null)} className="flex-1 border border-gray-300 py-3 rounded-xl text-sm">
                        Отмена
                      </button>
                    </div>
                    {closeRental.isError && (
                      <p className="text-red-500 text-xs text-center">
                        {(closeRental.error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Ошибка закрытия'}
                      </p>
                    )}
                  </div>
                )
              })()
            ) : (
              <button
                onClick={() => setClosingId(detailRental.id)}
                className="w-full border-2 border-green-300 text-green-700 font-semibold py-3 rounded-xl text-sm"
              >
                Закрыть аренду
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Rental closed summary */}
      {closedSummary && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end" onClick={() => setClosedSummary(null)}>
          <div className="bg-white w-full rounded-t-3xl p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-2xl">✅</span>
                <h3 className="font-bold text-lg text-green-700">Аренда закрыта</h3>
              </div>
              <button onClick={() => setClosedSummary(null)} className="text-gray-400 text-2xl leading-none">×</button>
            </div>

            <div className="bg-gray-50 rounded-2xl p-4 space-y-1">
              <p className="font-semibold">🚲 {closedSummary.bikeCodes}</p>
              <p className="text-sm text-gray-500">Клиент: {closedSummary.customerLabel}</p>
              <p className="text-xs text-gray-400">
                {closedSummary.pickupPoint}
                {closedSummary.returnPoint && closedSummary.returnPoint !== closedSummary.pickupPoint ? ` → ${closedSummary.returnPoint}` : ''}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3 text-center">
              {[['Выдача', closedSummary.startAt], ['Возврат', closedSummary.endAt], ['Время', formatDuration(closedSummary.durationMinutes)]].map(([label, val]) => (
                <div key={label} className="bg-blue-50 rounded-xl py-3">
                  <p className="text-xs text-blue-500 mb-0.5">{label}</p>
                  <p className="font-bold text-blue-700 text-sm">{val}</p>
                </div>
              ))}
            </div>

            {/* All operations for this rental, then the settlement (even 0), then total. */}
            <div className="border border-gray-200 rounded-2xl p-4 space-y-3">
              <p className="text-sm font-semibold text-gray-700">Операции по аренде</p>
              {closedSummary.paymentLines.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-400 border-b border-gray-100">
                      <th className="text-left pb-1">Операция</th>
                      <th className="text-left pb-1">Инвентарь</th>
                      <th className="text-left pb-1">Способ</th>
                      <th className="text-right pb-1">Сумма</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {closedSummary.paymentLines.map((pl, i) => {
                      // Cross-point top-up carries note 'Межточечный сбор' → «Межточка».
                      // Extra charge (damage) is flagged → «Доп. сбор».
                      const label = pl.isExtraCharge
                        ? 'Доп. сбор'
                        : pl.note && pl.note.toLowerCase().includes('межточ')
                        ? 'Межточка'
                        : ({ prepayment: 'Предоплата', topup: 'Оплата', refund: 'Возврат' } as Record<string, string>)[pl.type] ?? pl.type
                      return (
                        <tr key={i}>
                          <td className="py-1.5 text-gray-700">{label}</td>
                          <td className="py-1.5 text-gray-500">#{pl.bikeCodes}</td>
                          <td className="py-1.5 text-gray-500">{pl.paymentMethod ? (PAYMENT_LABEL[pl.paymentMethod] ?? pl.paymentMethod) : '—'}</td>
                          <td className={`py-1.5 text-right font-semibold ${pl.type === 'refund' || pl.amount < 0 ? 'text-emerald-700' : 'text-gray-800'}`}>
                            {pl.amount < 0 ? `−${Math.abs(pl.amount).toLocaleString('ru-RU')} ₽` : `${pl.amount.toLocaleString('ru-RU')} ₽`}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-gray-400 text-center">Нет записей об оплате</p>
              )}

              {/* Settlement headline — shown even when 0. */}
              {(() => {
                const s = closedSummary.settlement
                const type = s?.type ?? 'none'
                const amount = s?.amount ?? 0
                if (type === 'topup') {
                  return (
                    <div role="status" className="border-t pt-3 rounded-b-xl bg-red-50 -mx-4 -mb-4 px-4 py-3">
                      <p className="text-xs font-bold uppercase tracking-wide text-red-900">Доплата от клиента</p>
                      <p className="text-3xl font-extrabold text-red-700 tabular-nums mt-0.5">+{amount.toLocaleString('ru-RU')} ₽</p>
                    </div>
                  )
                }
                if (type === 'refund') {
                  return (
                    <div role="status" className="border-t pt-3 rounded-b-xl bg-emerald-50 -mx-4 -mb-4 px-4 py-3">
                      <p className="text-xs font-bold uppercase tracking-wide text-emerald-900">Возврат клиенту</p>
                      <p className="text-3xl font-extrabold text-emerald-800 tabular-nums mt-0.5">−{amount.toLocaleString('ru-RU')} ₽</p>
                    </div>
                  )
                }
                return (
                  <div role="status" className="border-t pt-3 rounded-b-xl bg-gray-50 -mx-4 -mb-4 px-4 py-3">
                    <p className="text-xs font-bold uppercase tracking-wide text-gray-600">Расчёт сошёлся</p>
                    <p className="text-3xl font-extrabold text-gray-700 tabular-nums mt-0.5">0 ₽</p>
                  </div>
                )
              })()}
            </div>

            <div className="px-1">
              <div className="flex justify-between items-baseline gap-3">
                <span className="font-semibold text-gray-800">Итого по аренде</span>
                <span className="font-bold text-lg text-green-700 tabular-nums">{closedSummary.totalPrice} ₽</span>
              </div>
              {closedSummary.crossPointFee > 0 && (
                <div className="flex justify-between text-xs text-gray-400 mt-0.5">
                  <span>в т.ч. межточка</span>
                  <span className="tabular-nums">{closedSummary.crossPointFee.toLocaleString('ru-RU')} ₽</span>
                </div>
              )}
            </div>

            <button onClick={() => setClosedSummary(null)} className="w-full bg-brand-600 text-white py-4 rounded-2xl font-bold">
              Готово
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
